import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { forgotPasswordApi, resetPasswordApi } from '@/utils/api'
import { useTranslation } from '@/i18n'

const PasswordResetPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [isResetMode, setIsResetMode] = useState(false)
  const [resetToken, setResetToken] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // 解析URL参数，检查是否在重置密码模式
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const emailParam = params.get('email')
    const tokenParam = params.get('token')

    if (emailParam && tokenParam) {
      setIsResetMode(true)
      setEmail(emailParam)
      setResetToken(tokenParam)
    }
  }, [location])

  // 处理发送重置邮件
  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    if (!email.trim()) {
      setError(t('passwordReset.error.invalidEmail'))
      setLoading(false)
      return
    }

    try {
      const response = await forgotPasswordApi({
        email
      })

      if (response.code === 200) {
        setSuccess(t('passwordReset.success.emailSent'))
        setEmail('') // 清空输入
      } else {
        setError(response.message || t('passwordReset.error.failedToSendEmail'))
      }
    } catch (err: any) {
      setError(err.message || t('passwordReset.error.failedToSendEmail'))
    } finally {
      setLoading(false)
    }
  }

  // 处理重置密码
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    // 基本验证
    if (!email || !resetToken) {
      setError(t('passwordReset.error.invalidResetLink'))
      setLoading(false)
      return
    }

    if (!newPassword.trim() || newPassword.length < 6) {
      setError(t('passwordReset.error.passwordTooShort'))
      setLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwordReset.error.passwordsDoNotMatch'))
      setLoading(false)
      return
    }

    try {
      const response = await resetPasswordApi({
        email,
        token: resetToken,
        new_password: newPassword
      })

      if (response.code === 200) {
        setSuccess(t('passwordReset.success.passwordReset'))
        // 3秒后自动跳转到登录页
        setTimeout(() => {
          navigate('/login', {
            state: { message: t('passwordReset.success.passwordReset') }
          })
        }, 3000)
      } else {
        setError(response.message || t('passwordReset.error.failedToResetPassword'))
      }
    } catch (err: any) {
      if (err.message?.includes('无效的验证链接') || err.message?.includes('验证链接已过期')) {
        setError(t('passwordReset.error.invalidResetLink'))
      } else {
        setError(err.message || t('passwordReset.error.failedToResetPassword'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600 bg-clip-text text-transparent mb-2">
            VisSynth
          </h1>
          <p className="text-gray-400">{t('passwordReset.platformDescription')}</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-sm text-green-400">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Send Reset Email Form */}
        {!isResetMode ? (
          <form className="mt-8 space-y-6" onSubmit={handleSendResetEmail}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                {t('passwordReset.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-950 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {t('passwordReset.sendingEmail')}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    {t('passwordReset.sendResetEmail')}
                  </div>
                )}
              </button>
            </div>

            {/* Back to Login */}
            <div className="text-center">
              <p className="text-sm text-gray-400">
                {t('passwordReset.backToLogin')}{' '}
                <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                  {t('passwordReset.login')}
                </Link>
              </p>
            </div>
          </form>
        ) : (
          // Reset Password Form
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('passwordReset.newPassword')}
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  required
                  className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('passwordReset.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-950 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {t('passwordReset.resettingPassword')}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    {t('passwordReset.resetPassword')}
                  </div>
                )}
              </button>
            </div>

            {/* Back to Login */}
            <div className="text-center">
              <p className="text-sm text-gray-400">
                {t('passwordReset.backToLogin')}{' '}
                <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                  {t('passwordReset.login')}
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default PasswordResetPage
